import React, { useState, useEffect } from 'react';
import { PlayCircle, BookOpen, Search, RefreshCw, Key } from 'lucide-react';
import useUnitCode from '../hooks/useUnitCode';
import UnitCard from '../components/unit/UnitCard';
import { MyUnitCodeDto } from '../types/unit';
import { useNavigate, Link } from 'react-router-dom';
import UnitCodePopup from '../components/common/UnitCodePopup';

const MyLectures: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const {
    units,
    isLoading,
    error,
    totalCount,
    totalPages,
    currentPage,
    getMyUnits,
    clearError,
    refreshUnits,
  } = useUnitCode();

  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUnits, setFilteredUnits] = useState<MyUnitCodeDto[]>([]);
  const [isUnitCodePopupOpen, setIsUnitCodePopupOpen] = useState(false);

  useEffect(() => {
    let filtered = units;

    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (unit) =>
          unit.unitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          unit.unitTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (unit.courseName && unit.courseName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredUnits(filtered);
  }, [units, searchTerm]);

  const handlePageChange = (page: number) => {
    getMyUnits(page);
  };

  const handleRefresh = () => {
    clearError();
    refreshUnits();
  };

  const handleUnitCodeSuccess = () => {
    refreshUnits();
    setIsUnitCodePopupOpen(false);
  };

  const handleUnitClick = (unitId: number) => {
    navigate(`/unit-viewer/${unitId}`);
  };

  const UnitCardShimmer = () => (
    <div className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="h-1 w-full bg-slate-200" />
      <div className="space-y-3 p-4 sm:p-5">
        <div className="h-5 w-2/3 rounded-lg bg-slate-200" />
        <div className="h-4 w-1/2 rounded-lg bg-slate-100" />
        <div className="flex gap-2">
          <div className="h-6 w-20 rounded-full bg-slate-100" />
          <div className="h-6 w-16 rounded-full bg-slate-100" />
        </div>
        <div className="h-11 w-full rounded-xl bg-slate-200" />
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#001848]">
        <PlayCircle className="h-8 w-8 text-white" />
      </div>
      <h3 className="text-lg font-bold text-[#001848]">لا توجد حصص بعد</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
        لم تشترك في أي حصص حتى الآن. تصفّح الحصص المتاحة واشترك للوصول إلى المحتوى.
      </p>
      <Link
        to="/student-portal/browse"
        className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-xl bg-gold-500 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-gold-400"
      >
        <BookOpen className="h-4 w-4" />
        تصفّح الحصص والكورسات
      </Link>
    </div>
  );

  const ErrorState = () => (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-12 text-center">
      <h3 className="mb-2 text-lg font-bold text-[#001848]">لا توجد حصص مسجّلة</h3>
      <p className="mb-6 text-sm text-slate-600">{error}</p>
      <button
        type="button"
        onClick={handleRefresh}
        className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#001848] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#002454]"
      >
        <RefreshCw className="h-4 w-4" />
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#F4F6FA]'} dir="rtl">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${embedded ? 'max-w-4xl py-2' : 'max-w-4xl py-8'}`}>
        {!embedded && (
          <div className="mb-8 text-center sm:mb-10 sm:text-start">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 sm:text-sm">
              <PlayCircle className="h-3.5 w-3.5" />
              حصصي
            </span>
            <h1 className="mb-2 text-2xl font-bold text-[#001848] font-cairo-bold sm:text-3xl">حصصي</h1>
            <p className="text-sm text-slate-500 sm:text-base">الحصص اللي اشتركت فيها — ادخل وكمّل تعلمك</p>
          </div>
        )}

        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:mb-6 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="البحث في حصصك..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="min-h-11 w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pe-10 ps-3 text-sm text-[#001848] outline-none transition focus:border-gold-400 focus:bg-white focus:ring-2 focus:ring-gold-200"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsUnitCodePopupOpen(true)}
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#001848] px-4 text-sm font-semibold text-white transition hover:bg-[#002454] sm:flex-none"
              >
                <Key className="h-4 w-4" />
                <span className="sm:inline">كود محاضرة</span>
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                disabled={isLoading}
                aria-label="تحديث"
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-[#001848] transition hover:border-gold-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {!isLoading && !error && (
          <p className="mb-4 text-center text-sm text-slate-500 sm:mb-5 sm:text-start">
            {filteredUnits.length > 0 ? (
              <>
                عرض <span className="font-bold text-[#001848]">{filteredUnits.length}</span> من أصل{' '}
                <span className="font-bold text-gold-600">{totalCount}</span> حصة
              </>
            ) : searchTerm ? (
              'لا توجد نتائج مطابقة للبحث'
            ) : (
              'لا توجد وحدات متاحة'
            )}
          </p>
        )}

        {error ? (
          <ErrorState />
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-5">
            {Array.from({ length: 6 }).map((_, index) => (
              <UnitCardShimmer key={`shimmer-${index}`} />
            ))}
          </div>
        ) : filteredUnits.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-5">
            {filteredUnits.map((unit) => (
              <div
                key={unit.unitId}
                onClick={() => handleUnitClick(unit.unitId)}
                className="cursor-pointer"
              >
                <UnitCard unit={unit} />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !error && totalPages > 1 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`min-h-10 rounded-xl px-4 text-sm font-semibold transition ${
                currentPage === 1
                  ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                  : 'border border-slate-200 bg-white text-[#001848] hover:border-gold-400'
              }`}
            >
              السابق
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={`min-h-10 min-w-10 rounded-xl px-3 text-sm font-bold transition ${
                    currentPage === page
                      ? 'bg-gold-500 text-white shadow-md shadow-gold-900/15'
                      : 'border border-slate-200 bg-white text-slate-600 hover:border-gold-400 hover:text-[#001848]'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`min-h-10 rounded-xl px-4 text-sm font-semibold transition ${
                currentPage === totalPages
                  ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                  : 'border border-slate-200 bg-white text-[#001848] hover:border-gold-400'
              }`}
            >
              التالي
            </button>
          </div>
        )}
      </div>

      <UnitCodePopup
        isOpen={isUnitCodePopupOpen}
        onClose={() => setIsUnitCodePopupOpen(false)}
        onSuccess={handleUnitCodeSuccess}
      />
    </div>
  );
};

export default MyLectures;
